import { BaseGroupIdProps } from "@custom-types/base-types";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { useAppParams } from "@router/router-helper";
import {
  companyMemberGroupDetailSelector,
  companyMemberGroupDetailStatusSelector,
} from "@store/company-member-groups/company-member-groups-selector";
import {
  CompanyMemberGroupEntity,
  removeProjectsFromGroup,
} from "@store/company-member-groups/company-member-groups-slice";
import { fetchCompanyMemberGroupDetails } from "@store/company-member-groups/company-member-groups-thunk";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { useCallback, useEffect } from "react";

interface UseMemberGroupDetails {
  /** Details of a group that the member belongs to */
  groupDetails: CompanyMemberGroupEntity | null;

  /** Whether the group details are currently being fetched */
  isFetchingGroupDetails: boolean;

  /** Optimistically remove projects from the local group details by ID */
  removeProjects(projectIds: string[]): void;
}

/** Fetches the details of group of a company member */
export function useMemberGroupDetails({
  groupId,
}: BaseGroupIdProps): UseMemberGroupDetails {
  const dispatch = useAppDispatch();
  const { memberId, teamId } = useAppParams();
  const { handleErrorWithToast } = useErrorContext();

  const groupDetails = useAppSelector(
    companyMemberGroupDetailSelector(groupId)
  );
  const groupDetailsStatus = useAppSelector(
    companyMemberGroupDetailStatusSelector(groupId)
  );

  useEffect(() => {
    async function fetchDetails(): Promise<void> {
      const id = memberId || teamId;
      if (!id) {
        return;
      }

      try {
        await dispatch(
          fetchCompanyMemberGroupDetails({ groupId, userIdentity: id })
        ).unwrap();
      } catch (error) {
        handleErrorWithToast({
          id: `getCompanyMemberGroupDetails-${groupId}`,
          title: "Cannot fetch workspace member group details",
          error,
        });
      }
    }

    void fetchDetails();
  }, [dispatch, groupId, handleErrorWithToast, memberId, teamId]);

  const removeProjects = useCallback(
    (projectIds: string[]) => {
      dispatch(removeProjectsFromGroup({ groupId, projectIds }));
    },
    [dispatch, groupId]
  );

  return {
    groupDetails,
    isFetchingGroupDetails: groupDetailsStatus === "pending",
    removeProjects,
  };
}
